"use client";

import { c as _c } from "react/compiler-runtime";
import EnquirySheetShell from "./EnquirySheetShell";
import MandatoryFormSheet from "./MandatoryFormSheet";
import SurveyBody from "@/components/survey/SurveyBody";
import ReviewSheet from "@/components/review/ReviewSheet";
import SubmittedBody from "@/components/review/SubmittedBody";
import { useEnquiryFlowStore } from "@/store/enquiryFlowStore";
import { useSurveySheetStore } from "@/store/surveySheetStore";
import { leaveFlow, useEnquiryFlowHistory } from "./enquiryFlowHistory";

// The single physical sidesheet for the whole mandatory-form -> survey ->
// review -> success chain. Rather than each step popping its own separate
// Dialog/Drawer (which looked like one sheet closing and another one
// opening), all four stay mounted together here and only the "which page is
// in view" position changes, sliding horizontally between them — one
// continuous sheet, one backdrop, one open/close animation.
//
// The survey step is hosted here only when it was opened as part of THIS
// flow (surveySheetStore's context === "enquiryFlow"); a completely
// unrelated caller still gets its own independent sheet via SurveySheet.
export default function EnquiryFlowShell() {
  const $ = _c(16);
  const activeStep = useEnquiryFlowStore(_temp);
  const surveyIsOpen = useSurveySheetStore(_temp2);
  const surveyContext = useSurveySheetStore(_temp3);
  const surveyInFlow = surveyIsOpen && surveyContext === "enquiryFlow";
  const isOpen = activeStep !== null || surveyInFlow;
  useEnquiryFlowHistory(surveyIsOpen ? "survey" : activeStep);
  let currentIndex;
  if ($[0] !== activeStep || $[1] !== surveyInFlow) {
    currentIndex = 0;
    if (surveyInFlow) {
      currentIndex = 1;
    } else {
      if (activeStep === "review") {
        currentIndex = 2;
      } else {
        if (activeStep === "success") {
          currentIndex = 3;
        }
      }
    }
    $[0] = activeStep;
    $[1] = surveyInFlow;
    $[2] = currentIndex;
  } else {
    currentIndex = $[2];
  }
  const handleDismiss = _temp4;
  let t0;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MandatoryFormSheet key="mandatory" />;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== surveyInFlow) {
    t1 = <SurveyBody key="survey" isActive={surveyInFlow} />;
    $[4] = surveyInFlow;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ReviewSheet key="review" />;
    t3 = <SubmittedBody key="success" />;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1) {
    t4 = [t0, t1, t2, t3];
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const pages = t4;
  let t5;
  if ($[10] !== currentIndex || $[11] !== pages) {
    t5 = <div className="relative h-full w-full overflow-hidden">{pages.map((page, index) => <div key={page.key} className={`absolute inset-0 h-full w-full transition-transform duration-300 ease-in-out ${index === currentIndex ? "" : "pointer-events-none"}`} style={{
        transform: index === currentIndex ? "none" : `translateX(${(index - currentIndex) * 100}%)`
      }} aria-hidden={index !== currentIndex}>{page}</div>)}</div>;
    $[10] = currentIndex;
    $[11] = pages;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isOpen || $[14] !== t5) {
    t6 = <EnquirySheetShell open={isOpen} onClose={handleDismiss} width={480}>{t5}</EnquirySheetShell>;
    $[13] = isOpen;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp4() {
  return leaveFlow();
}
function _temp3(state_1) {
  return state_1.context;
}
function _temp2(state_0) {
  return state_0.isOpen;
}
function _temp(state) {
  return state.activeStep;
}